/**
 * cabinModules.js — React Query hooks for 5 cabin module admin CRUD APIs
 *
 * Endpoints (all under /admin/):
 *   voice-practice/scenarios
 *   voice-interview/questions
 *   aviation-english/scenarios
 *   aviation-english/questions
 *   announce-templates
 *   pax-comm/situations
 *
 * Backend branch: feature/admin-cabin-modules-crud (parallel development).
 * All hooks degrade gracefully (empty array) until endpoints go live.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

// ─── Query key factory ────────────────────────────────────────────────────────

export const cabinModuleKeys = {
  // Voice Practice Scenarios
  voiceScenarios: {
    all: ['voiceScenarios'],
    lists: () => [...cabinModuleKeys.voiceScenarios.all, 'list'],
    list: (filters) => [...cabinModuleKeys.voiceScenarios.lists(), filters],
    detail: (id) => [...cabinModuleKeys.voiceScenarios.all, 'detail', id],
  },
  // Voice Interview Questions
  voiceInterviews: {
    all: ['voiceInterviews'],
    lists: () => [...cabinModuleKeys.voiceInterviews.all, 'list'],
    list: (filters) => [...cabinModuleKeys.voiceInterviews.lists(), filters],
    detail: (id) => [...cabinModuleKeys.voiceInterviews.all, 'detail', id],
  },
  // Aviation English Scenarios
  aviationScenarios: {
    all: ['aviationScenarios'],
    lists: () => [...cabinModuleKeys.aviationScenarios.all, 'list'],
    list: (filters) => [...cabinModuleKeys.aviationScenarios.lists(), filters],
    detail: (id) => [...cabinModuleKeys.aviationScenarios.all, 'detail', id],
  },
  // Aviation English Questions
  aviationQuestions: {
    all: ['aviationQuestions'],
    lists: () => [...cabinModuleKeys.aviationQuestions.all, 'list'],
    list: (filters) => [...cabinModuleKeys.aviationQuestions.lists(), filters],
  },
  // Announce Templates
  announceTemplates: {
    all: ['announceTemplates'],
    lists: () => [...cabinModuleKeys.announceTemplates.all, 'list'],
    list: (filters) => [...cabinModuleKeys.announceTemplates.lists(), filters],
    detail: (id) => [...cabinModuleKeys.announceTemplates.all, 'detail', id],
  },
  // Pax Comm Situations
  paxComm: {
    all: ['paxComm'],
    lists: () => [...cabinModuleKeys.paxComm.all, 'list'],
    list: (filters) => [...cabinModuleKeys.paxComm.lists(), filters],
    detail: (id) => [...cabinModuleKeys.paxComm.all, 'detail', id],
  },
};

// ─── Helper: build query params ───────────────────────────────────────────────

function buildParams(page = 1, filters = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', '20');
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  return params.toString();
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. VOICE PRACTICE SCENARIOS
// ═══════════════════════════════════════════════════════════════════════════════

export function useVoiceScenarios(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.voiceScenarios.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/voice-practice/scenarios?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreateVoiceScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/voice-practice/scenarios', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceScenarios.lists() }),
  });
}

export function useUpdateVoiceScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/voice-practice/scenarios/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceScenarios.lists() }),
  });
}

export function useDeleteVoiceScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/voice-practice/scenarios/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceScenarios.lists() }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. VOICE INTERVIEW QUESTIONS
// ═══════════════════════════════════════════════════════════════════════════════

export function useVoiceInterviewQuestions(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.voiceInterviews.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/voice-interview/questions?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreateVoiceInterviewQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/voice-interview/questions', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceInterviews.lists() }),
  });
}

export function useUpdateVoiceInterviewQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/voice-interview/questions/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceInterviews.lists() }),
  });
}

export function useDeleteVoiceInterviewQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/voice-interview/questions/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.voiceInterviews.lists() }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. AVIATION ENGLISH SCENARIOS
// ═══════════════════════════════════════════════════════════════════════════════

export function useAviationScenarios(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.aviationScenarios.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/aviation-english/scenarios?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreateAviationScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/aviation-english/scenarios', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationScenarios.lists() }),
  });
}

export function useUpdateAviationScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/aviation-english/scenarios/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationScenarios.lists() }),
  });
}

export function useDeleteAviationScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/aviation-english/scenarios/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationScenarios.lists() }),
  });
}

// ─── Aviation Questions ───────────────────────────────────────────────────────

export function useAviationQuestions(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.aviationQuestions.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/aviation-english/questions?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreateAviationQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/aviation-english/questions', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationQuestions.lists() });
      qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationScenarios.lists() });
    },
  });
}

export function useUpdateAviationQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/aviation-english/questions/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationQuestions.lists() }),
  });
}

export function useDeleteAviationQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/aviation-english/questions/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.aviationQuestions.lists() }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. ANNOUNCE TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════════

export function useAnnounceTemplates(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.announceTemplates.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/announce-templates?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreateAnnounceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/announce-templates', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.announceTemplates.lists() }),
  });
}

export function useUpdateAnnounceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/announce-templates/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.announceTemplates.lists() }),
  });
}

export function useDeleteAnnounceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/announce-templates/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.announceTemplates.lists() }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. PAX COMM SITUATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export function usePaxCommSituations(page = 1, filters = {}) {
  return useQuery({
    queryKey: cabinModuleKeys.paxComm.list({ page, ...filters }),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/pax-comm/situations?${buildParams(page, filters)}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useCreatePaxCommSituation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/admin/pax-comm/situations', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.paxComm.lists() }),
  });
}

export function useUpdatePaxCommSituation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/pax-comm/situations/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.paxComm.lists() }),
  });
}

export function useDeletePaxCommSituation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/pax-comm/situations/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinModuleKeys.paxComm.lists() }),
  });
}
